/**
 * ERPat realtime envelope v1 — the Syntry copy of the shared wire contract.
 *
 * The canonical schema lives beside this file as
 * `realtime-envelope-v1.schema.json` (with golden fixtures in
 * `realtime-envelope-v1.fixtures.json`); both are BYTE-IDENTICAL copies of the
 * specification's contract files and must never be edited here. This module
 * mirrors that schema in code: the `RealtimeEnvelope` type is what the rest of
 * the app consumes, and {@link parseRealtimeEnvelope} is the single gate every
 * frame from the gateway passes through before anything renders it.
 *
 * Phase 1 consumer rules (spec §2), all enforced here:
 *  - `data` on the Pusher frame is a JSON STRING; pusher-js decodes it, but a
 *    string is still accepted for robustness.
 *  - Unknown TOP-LEVEL keys are ignored (the schema allows additive fields).
 *  - An envelope whose `schema_version` is not in
 *    {@link SUPPORTED_EVENT_VERSIONS} or whose `type` is unknown is NOT rendered
 *    (returns `null`; the caller logs at debug level).
 *  - `action.route` is a dotted key; one that is not in
 *    {@link REALTIME_ACTION_ROUTES} is downgraded to `action: null` (fall back
 *    to the inbox), while a value that breaks the schema pattern — a URL, for
 *    instance — rejects the whole envelope.
 *  - Titles/bodies are plain text. The parser preserves them verbatim (it does
 *    NOT strip markup); escaping is the renderer's job — mustache interpolation
 *    or `textContent`, never `v-html`.
 *
 * DELIBERATELY IMPORT-FREE. `scripts/verify-realtime-fixtures.mjs` loads this
 * file straight into Node (native type stripping) to run the golden fixtures
 * through the real parser — no bundler, no extra dev dependency. A relative
 * import would need an explicit `.ts` extension for Node, which this tsconfig
 * forbids, so everything the parser needs is defined in this one file.
 */

/** The Pusher event name every envelope arrives under. */
export const EVENT_NAME = 'erpat-realtime-v1' as const;

/**
 * Schema versions this client renders. Exactly `[1]` for now; a future `[2, 1]`
 * only appears with a separately approved schema-v2 rollout (spec "N/N-1").
 */
export const SUPPORTED_EVENT_VERSIONS: readonly number[] = [1];

/** Envelope types the schema enumerates. Anything else is unknown → not rendered. */
export const REALTIME_ENVELOPE_TYPES = [
  'notification.created',
  'notification.read',
  'notification.dismissed',
  'message.created',
  'message.read',
] as const;
export type RealtimeEnvelopeType = (typeof REALTIME_ENVELOPE_TYPES)[number];

export const REALTIME_PRIVACY_CLASSES = ['private', 'generic'] as const;
export type RealtimePrivacyClass = (typeof REALTIME_PRIVACY_CLASSES)[number];

/**
 * The Phase 1 action-route allowlist (spec §6). The mapping from a route to a
 * router location lives in `realtime-routes.ts`; this list is what the parser
 * uses to decide whether an `action` is honoured at all.
 */
export const REALTIME_ACTION_ROUTES = [
  'notifications.inbox',
  'notifications.view',
  'messages.inbox',
  'messages.thread',
  'advisories.view',
] as const;
export type RealtimeActionRoute = (typeof REALTIME_ACTION_ROUTES)[number];

/**
 * Top-level keys the schema marks `required`, mirrored so the fixture script
 * can detect drift between the JSON contract and this parser.
 */
export const REALTIME_ENVELOPE_REQUIRED_KEYS = [
  'schema_version',
  'event_id',
  'type',
  'occurred_at',
  'tenant_scope',
  'recipient_id',
  'sequence',
  'state_revision',
  'privacy_class',
  'actor',
  'notification',
  'resource',
  'action',
] as const;

export interface RealtimeActor {
  /** Decimal user id, or the literal `system`. */
  id: string;
  display_name: string;
  avatar_url: string | null;
}

export interface RealtimeNotification {
  /** Decimal notification id, or a UUID for gateway-originated test events. */
  id: string;
  category: string;
  /** Plain text — render as a text node, never as HTML. */
  title: string;
  /** Plain text — render as a text node, never as HTML. */
  body: string;
}

export interface RealtimeResource {
  type: string;
  id: string;
}

export interface RealtimeAction {
  route: RealtimeActionRoute;
  params: Readonly<Record<string, string>>;
}

export interface RealtimeEnvelope {
  schema_version: number;
  event_id: string;
  type: RealtimeEnvelopeType;
  /** RFC3339 UTC with a `Z` suffix. */
  occurred_at: string;
  tenant_scope: string;
  recipient_id: string;
  /** Decimal string; `"0"` until the durable stream exists. */
  sequence: string;
  /** Opaque; `null` until the durable stream exists. */
  cursor: string | null;
  state_revision: number;
  privacy_class: RealtimePrivacyClass;
  actor: RealtimeActor | null;
  notification: RealtimeNotification | null;
  resource: RealtimeResource | null;
  action: RealtimeAction | null;
}

/* ── Schema patterns (verbatim from realtime-envelope-v1.schema.json) ──────── */

const EVENT_ID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;
const OCCURRED_AT_RE = /^[0-9]{4}-[0-9]{2}-[0-9]{2}T[0-9]{2}:[0-9]{2}:[0-9]{2}(\.[0-9]{1,6})?Z$/;
const TENANT_SCOPE_RE = /^t_[A-Za-z0-9_-]{1,64}$/;
const DECIMAL_ID_RE = /^[0-9]{1,20}$/;
const ACTOR_ID_RE = /^[0-9]{1,20}$|^system$/;
const NOTIFICATION_ID_RE = /^[0-9]{1,20}$|^[0-9a-f-]{36}$/;
const TOKEN_RE = /^[a-z0-9_]{1,64}$/;
const ACTION_ROUTE_RE = /^[a-z0-9_]+(\.[a-z0-9_]+)*$/;

const MAX_DISPLAY_NAME = 191;
const MAX_AVATAR_URL = 2048;
const MAX_TITLE = 191;
const MAX_BODY = 1000;
const MAX_RESOURCE_ID = 191;
const MAX_CURSOR = 4096;
const MAX_ROUTE = 64;
const MAX_PARAM_VALUE = 191;
const MAX_PARAMS = 16;

/* ── Small guards ──────────────────────────────────────────────────────────── */

type Rejection = { ok: false; reason: string };
type Accepted<T> = { ok: true; value: T };
type Result<T> = Accepted<T> | Rejection;

const reject = (reason: string): Rejection => ({ ok: false, reason });
const accept = <T>(value: T): Accepted<T> => ({ ok: true, value });

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isInteger(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value);
}

/** `value` is a string no longer than `max` code units (the schema's `maxLength`). */
function isBoundedString(value: unknown, max: number): value is string {
  return typeof value === 'string' && value.length <= max;
}

function isPatterned(value: unknown, re: RegExp, max?: number): value is string {
  return typeof value === 'string' && (max === undefined || value.length <= max) && re.test(value);
}

/* ── Sub-object parsers (each nullable per the schema) ─────────────────────── */

function parseActor(value: unknown): Result<RealtimeActor | null> {
  if (value === null) return accept(null);
  if (!isRecord(value)) return reject('actor must be an object or null');
  if (!isPatterned(value.id, ACTOR_ID_RE)) return reject('actor.id');
  if (!isBoundedString(value.display_name, MAX_DISPLAY_NAME)) return reject('actor.display_name');
  const avatar = value.avatar_url;
  if (avatar !== undefined && avatar !== null && !isBoundedString(avatar, MAX_AVATAR_URL)) {
    return reject('actor.avatar_url');
  }
  return accept({
    id: value.id,
    display_name: value.display_name,
    avatar_url: typeof avatar === 'string' ? avatar : null,
  });
}

function parseNotification(value: unknown): Result<RealtimeNotification | null> {
  if (value === null) return accept(null);
  if (!isRecord(value)) return reject('notification must be an object or null');
  if (!isPatterned(value.id, NOTIFICATION_ID_RE)) return reject('notification.id');
  if (!isPatterned(value.category, TOKEN_RE)) return reject('notification.category');
  if (!isBoundedString(value.title, MAX_TITLE)) return reject('notification.title');
  if (!isBoundedString(value.body, MAX_BODY)) return reject('notification.body');
  // Title/body are copied verbatim on purpose: the schema says "plain text",
  // and stripping here would hide a producer bug instead of surfacing it. The
  // renderer escapes; the fixture `html_in_title_…` pins that split.
  return accept({
    id: value.id,
    category: value.category,
    title: value.title,
    body: value.body,
  });
}

function parseResource(value: unknown): Result<RealtimeResource | null> {
  if (value === null) return accept(null);
  if (!isRecord(value)) return reject('resource must be an object or null');
  if (!isPatterned(value.type, TOKEN_RE)) return reject('resource.type');
  if (!isBoundedString(value.id, MAX_RESOURCE_ID)) return reject('resource.id');
  return accept({ type: value.type, id: value.id });
}

function isAllowlistedRoute(route: string): route is RealtimeActionRoute {
  return (REALTIME_ACTION_ROUTES as readonly string[]).includes(route);
}

/**
 * `action` is the one sub-object with `additionalProperties: false`, and the
 * one place the contract distinguishes "schema-invalid" (reject the envelope)
 * from "valid but not allowlisted" (keep the envelope, drop the action).
 */
function parseAction(value: unknown): Result<RealtimeAction | null> {
  if (value === null) return accept(null);
  if (!isRecord(value)) return reject('action must be an object or null');
  for (const key of Object.keys(value)) {
    if (key !== 'route' && key !== 'params') return reject(`action.${key} is not allowed`);
  }
  if (!isPatterned(value.route, ACTION_ROUTE_RE, MAX_ROUTE)) return reject('action.route');
  if (!isRecord(value.params)) return reject('action.params');
  const entries = Object.entries(value.params);
  if (entries.length > MAX_PARAMS) return reject('action.params has too many entries');
  const params: Record<string, string> = {};
  for (const [key, param] of entries) {
    if (!isBoundedString(param, MAX_PARAM_VALUE)) return reject(`action.params.${key}`);
    params[key] = param;
  }
  // Schema-valid but unknown to this client: fall back to "open the inbox"
  // rather than refusing a notification the user should still see.
  if (!isAllowlistedRoute(value.route)) return accept(null);
  return accept({ route: value.route, params });
}

/* ── Envelope ──────────────────────────────────────────────────────────────── */

function validate(raw: unknown): Result<RealtimeEnvelope> {
  let input: unknown = raw;
  if (typeof input === 'string') {
    // Pusher's `data` is a JSON string; pusher-js decodes it for us, but a
    // caller (or a double-encoded producer) may still hand us the string form.
    try {
      input = JSON.parse(input);
    } catch {
      return reject('data is not valid JSON');
    }
  }
  if (!isRecord(input)) return reject('envelope must be an object');

  for (const key of REALTIME_ENVELOPE_REQUIRED_KEYS) {
    if (!(key in input)) return reject(`required field ${key} absent`);
  }

  if (!isInteger(input.schema_version) || !SUPPORTED_EVENT_VERSIONS.includes(input.schema_version)) {
    return reject('schema_version unsupported');
  }
  if (!isPatterned(input.event_id, EVENT_ID_RE)) return reject('event_id');
  if (typeof input.type !== 'string' || !(REALTIME_ENVELOPE_TYPES as readonly string[]).includes(input.type)) {
    return reject('type unknown');
  }
  if (!isPatterned(input.occurred_at, OCCURRED_AT_RE)) return reject('occurred_at');
  if (!isPatterned(input.tenant_scope, TENANT_SCOPE_RE)) return reject('tenant_scope');
  if (!isPatterned(input.recipient_id, DECIMAL_ID_RE)) return reject('recipient_id');
  if (!isPatterned(input.sequence, DECIMAL_ID_RE)) return reject('sequence');

  // `cursor` is optional in the schema (absent === null in Phase 1).
  const cursor = input.cursor;
  if (cursor !== undefined && cursor !== null && !isBoundedString(cursor, MAX_CURSOR)) {
    return reject('cursor');
  }

  if (!isInteger(input.state_revision) || input.state_revision < 1) return reject('state_revision');
  if (
    typeof input.privacy_class !== 'string' ||
    !(REALTIME_PRIVACY_CLASSES as readonly string[]).includes(input.privacy_class)
  ) {
    return reject('privacy_class');
  }

  const actor = parseActor(input.actor);
  if (!actor.ok) return actor;
  const notification = parseNotification(input.notification);
  if (!notification.ok) return notification;
  const resource = parseResource(input.resource);
  if (!resource.ok) return resource;
  const action = parseAction(input.action);
  if (!action.ok) return action;

  // Only the keys the contract defines are copied out — this is where unknown
  // additive top-level keys are dropped.
  return accept({
    schema_version: input.schema_version,
    event_id: input.event_id,
    type: input.type as RealtimeEnvelopeType,
    occurred_at: input.occurred_at,
    tenant_scope: input.tenant_scope,
    recipient_id: input.recipient_id,
    sequence: input.sequence,
    cursor: typeof cursor === 'string' ? cursor : null,
    state_revision: input.state_revision,
    privacy_class: input.privacy_class as RealtimePrivacyClass,
    actor: actor.value,
    notification: notification.value,
    resource: resource.value,
    action: action.value,
  });
}

/**
 * Parse one gateway frame payload into a typed envelope, or `null` when it
 * must not be rendered. `null` covers both a malformed payload and a version
 * or type this client does not understand — callers treat the two alike in
 * Phase 1 (log at debug level; later phases reconcile instead).
 */
export function parseRealtimeEnvelope(raw: unknown): RealtimeEnvelope | null {
  const result = validate(raw);
  return result.ok ? result.value : null;
}

/**
 * Why {@link parseRealtimeEnvelope} would return `null` for `raw`, as a short
 * content-free rule name (`"event_id"`, `"type unknown"`, …), or `null` when it
 * would be accepted. For debug logging and the fixture script only — never
 * shown to users, and never containing payload text.
 */
export function explainRealtimeEnvelopeRejection(raw: unknown): string | null {
  const result = validate(raw);
  return result.ok ? null : result.reason;
}
